// Library methods
import { useMemo, forwardRef, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

// MUI Components
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import {
  CopyIcon,
  DiagonalArrowsIcon,
  EditIcon,
  EllipsisIcon,
  LinkIcon,
  TransferIcon,
  ViewFileIcon,
  VRIcon,
} from "../../../../assets/icons";
import ExamMenu from "../../../../components/UI/ExamMenu";
import CustomCheckbox from "../../../../components/UI/CustomCheckbox";
import ExamCardShared from "../../../../components/UI/ExamCardShared";

// utilities
import {
  getTooltipText,
  getExamCardText,
  getCompletedExamsTooltip,
} from "../../../../utils/examReportsHelper";
import { getLocalDate } from "../../../../utils/dateHelper";
import { useLiveUpdateStatusContext } from "../../../../contexts/LiveUpdateStatusContext";
import { ExamTransferContext } from "../../../../contexts/ExamTransferContext";
import useClinic from "../../../../hooks/useClinic";

const ExamCard = forwardRef(
  (
    {
      row,
      patientId,
      isSelected,
      headCells,
      handleCheckboxClick,
      handleClickToGetFullObject,
      handleEditClick,
      handleLinkDeviceClick,
      handleUnlinkDeviceClick,
      handleDuplicateClick,
      associatedExamDevices,
      linkLoading,
      unlinkLoading,
    },
    ref
  ) => {
    const {
      exam,
      examStatus,
      device,
      hasDevice,
      testCompletionIcon,
      isEditDisabled,
      checkReportDisabled,
      checkLinkDeviceDisabled,
      checkTooltipDeviceMessage,
      checkLiveUpdateDisabled,
    } = ExamCardShared(row, associatedExamDevices);

    // internationalization
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { clinicTimezone } = useClinic();

    const { actions } = useLiveUpdateStatusContext();
    const { handleViewLiveUpdateClick } = actions;
    const { handleExamTransferClick } = useContext(ExamTransferContext);

    const isMultiExamPackage = exam?.sectionTypeOrder?.length > 1;

    const examTranslation = useMemo(() => getExamCardText(exam, t), [exam, t]);
    const examTooltipTranslation = useMemo(
      () => getTooltipText(exam, t),
      [exam, t]
    );
    const completedExamSections = useMemo(
      () => getCompletedExamsTooltip(exam, t),
      [exam, t]
    );

    const localSchedule = useMemo(
      () =>
        getLocalDate(
          exam?.scheduledDate ??
            DateTime.now()
              .setZone(clinicTimezone)
              .startOf("day")
              .toUTC()
              .toISO(),
          clinicTimezone
        ),
      [exam?.scheduledDate, clinicTimezone]
    );

    const [examMenu, setExamMenu] = useState(null);

    // menu click handler
    const examMenuClick = (event) => {
      setExamMenu(event.currentTarget);
    };

    // menu close handler
    const examMenuClose = () => {
      setExamMenu(null);
    };

    // buttons are ordered in terms of importance / usage
    const examMenuButtons = useMemo(() => {
      const allButtons = [
        {
          icon: <ViewFileIcon />,
          tooltipText: t("word_view_results"),
          text: t("button_view_report"),
          onClick: () => {},
          display: !checkReportDisabled,
          href: `/patients/${patientId}/exam/${exam?.id}`,
        },
        {
          icon: <EditIcon />,
          text: t("word_edit"),
          tooltipText: t("exams_edit_modal_title"),
          onClick: () => handleEditClick(exam),
          display: !isEditDisabled,
          href: undefined,
        },
        {
          icon: <CopyIcon />,
          text: t("button_duplicate"),
          tooltipText: t("tooltip_duplicate_exam"),
          onClick: () => handleDuplicateClick(exam),
          display: true,
          href: undefined,
        },
        {
          icon: <TransferIcon />,
          text: t("word_transfer"),
          tooltipText: t("tooltip_transfer_exam"),
          onClick: () => handleExamTransferClick(exam, patientId),
          display: !checkReportDisabled,
          href: undefined,
        },
      ];

      return allButtons?.filter((examButton) => examButton?.display);
    }, [
      t,
      exam,
      patientId,
      isEditDisabled,
      checkReportDisabled,
      handleDuplicateClick,
      handleEditClick,
      handleExamTransferClick,
    ]);

    // only display 3 icons total on the ExamCard component
    // if there are over 3 icons, display the ellipsis dropdown
    const displayEllipsisIcon = useMemo(() => {
      const visibleLiveUpdateIcon =
        examStatus?.status === t("word_completed") ||
        examStatus?.status === t("word_terminated")
          ? 0
          : 1;

      const otherVisibleIcons = examMenuButtons.filter(
        (examButton) => examButton.display
      )?.length;

      const totalIcons = visibleLiveUpdateIcon + otherVisibleIcons;
      return totalIcons > 3;
    }, [t, examStatus, examMenuButtons]);

    // slice the icon array so that:
    // 3 unique icons can be displayed on the ExamCard
    // if there are over 3 icons, display the remainder icons in the ellipsis dropdown
    const iconIndexToSlice = useMemo(() => {
      return displayEllipsisIcon ? 1 : examMenuButtons?.length;
    }, [displayEllipsisIcon, examMenuButtons]);

    // because of the device section being split, we needed to add a grid offset to each of the items inside the card
    return (
      <Box ref={ref}>
        <Grid
          container
          py={0}
          sx={{ display: "flex", alignItems: "center", flexWrap: "no-wrap" }}
        >
          <Grid item xs={headCells[0].gridSize + 1} sm={headCells[0].gridSize}>
            {!hasDevice && (
              <Tooltip
                title={checkTooltipDeviceMessage()}
                PopperProps={{
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -10],
                      },
                    },
                  ],
                }}
              >
                <Card
                  disabled={checkLinkDeviceDisabled() || linkLoading}
                  elevation={0}
                  key={exam.id}
                  sx={{
                    mb: 2,
                    mr: 2,
                    borderRadius: 2,
                    backgroundColor: "#E2772E",
                    "&:hover": {
                      backgroundColor: "#CE631A",
                    },
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    color: "white",
                    height: { xs: 120, sm: 52 },
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                    cursor: "pointer",
                    flexWrap: "wrap",
                    // when the card is disabled, add change cursor and add a grey backgroundColor
                    ...(checkLinkDeviceDisabled() && {
                      cursor: "auto",
                      backgroundColor: "#5B6D7A70",
                      "&:hover": {
                        backgroundColor: "#5B6D7A70",
                      },
                    }),
                  }}
                  // only enable click method when the card is not disabled
                  {...(!checkLinkDeviceDisabled() &&
                    !linkLoading && {
                      onClick: () => handleLinkDeviceClick(exam),
                    })}
                  data-testid="link-card-exam-card"
                >
                  <VRIcon />
                  <LinkIcon />
                </Card>
              </Tooltip>
            )}
            {hasDevice && (
              <Tooltip title={checkTooltipDeviceMessage()}>
                <Card
                  elevation={0}
                  key={exam.id}
                  sx={{
                    mb: 2,
                    mr: 2,
                    borderRadius: 2,
                    backgroundColor: "#202338",
                    color: "white",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                    height: { xs: 120, sm: 52 },
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    cursor: "pointer",
                    px: 1,
                  }}
                  disabled={unlinkLoading}
                  onClick={() => handleUnlinkDeviceClick(device)}
                  data-testid="unlink-card-exam-card"
                >
                  <VRIcon />
                  <Typography
                    sx={{
                      display: { xs: "none", sm: "block", md: "block" },
                      width: "100%",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      overflow: "hidden",
                    }}
                  >
                    {device?.alias}
                  </Typography>
                </Card>
              </Tooltip>
            )}
          </Grid>
          <Grid
            item
            xs={12 - headCells[0].gridSize - 1}
            sm={12 - headCells[0].gridSize}
          >
            <Card
              elevation={0}
              key={exam.id}
              sx={{
                mb: "16px",
                py: "8px",
                borderRadius: "8px",
                backgroundColor: isSelected ? "#E2772E" : "#202338",
                color: "#FFF",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.15)",
                height: { xs: 120, sm: 52 },
                display: "flex",
                jutifyContent: "center",
                alignItems: "center",
              }}
            >
              <Grid
                container
                py={0}
                sx={{
                  display: { xs: "grid", sm: "flex" },
                  gridTemplateColumns: { xs: "13% 57% 30%", sm: "none" },
                  alignItems: "center",
                  justifyContent: "space-around",
                }}
              >
                <Grid item sm={1} sx={{ ml: { xs: 0, sm: 0 } }}>
                  <CustomCheckbox
                    checkedFill="#E2772E"
                    checked={isSelected}
                    onClick={(event) => {
                      handleCheckboxClick(event, exam.id);
                      handleClickToGetFullObject(event, exam);
                    }}
                  />
                </Grid>
                <Grid
                  item
                  sm={9}
                  display={{ sx: "block", sm: "flex" }}
                  flexDirection={{ sm: "space-between" }}
                  alignItems="center"
                >
                  <Grid item xs={12} sm={headCells[1].gridSize}>
                    <Typography
                      sx={{
                        display: { xs: "block", sm: "none" },
                        fontSize: { xs: "12px", md: "16px" },
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <b>{headCells[1].label}:</b> {examStatus?.status}
                    </Typography>
                    <Tooltip
                      disableHoverListener={
                        !isMultiExamPackage ||
                        !examStatus?.status.includes(" / ")
                      }
                      title={
                        <div style={{ whiteSpace: "pre-line" }}>
                          {completedExamSections}
                        </div>
                      }
                      placement="bottom-start"
                    >
                      <span>
                        <Typography
                          sx={{
                            display: { xs: "none", sm: "block" },
                            fontSize: { xs: "12px", md: "16px" },
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {examStatus?.status}
                        </Typography>
                      </span>
                    </Tooltip>
                  </Grid>
                  <Grid item xs={12} sm={headCells[2].gridSize}>
                    <Box
                      sx={{
                        display: { xs: "block", sm: "none", md: "none" },
                      }}
                    >
                      <Box
                        display="flex"
                        sx={{
                          fontSize: {
                            xs: "12px",
                            md: "16px",
                          },
                        }}
                      >
                        <b style={{ marginRight: 4 }}>{headCells[2].label}:</b>
                        {testCompletionIcon}
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        display: { xs: "none", sm: "block", md: "block" },
                        fontSize: { xs: "12px", md: "16px" },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {testCompletionIcon}
                    </Box>
                  </Grid>
                  <Tooltip
                    title={
                      <div style={{ whiteSpace: "pre-line" }}>
                        {examTooltipTranslation}
                      </div>
                    }
                    placement="bottom-start"
                  >
                    <Grid item xs={12} sm={headCells[3].gridSize}>
                      <Typography
                        sx={{
                          display: { xs: "block", sm: "none", md: "none" },
                          fontSize: {
                            xs: "12px",
                            md: "16px",
                          },
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <b>{headCells[3].label}: </b>
                        {examTranslation}
                      </Typography>
                      <Typography
                        sx={{
                          display: { xs: "none", sm: "block", md: "block" },
                          fontSize: {
                            xs: "12px",
                            md: "16px",
                          },
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {examTranslation}
                      </Typography>
                    </Grid>
                  </Tooltip>
                  <Grid item xs={12} sm={headCells[4].gridSize}>
                    <Typography
                      sx={{
                        display: { xs: "block", sm: "none", md: "none" },
                        fontSize: { xs: "12px", md: "16px" },
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <b>{headCells[4].label}:</b> {localSchedule.slice(0, 10)}
                    </Typography>
                    <Typography
                      sx={{
                        display: { xs: "none", sm: "block", md: "block" },
                        fontSize: { xs: "12px", md: "16px" },
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {localSchedule.slice(0, 10)}
                    </Typography>
                  </Grid>
                </Grid>
                <Grid item sm={2}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: { xs: "center", sm: "end" },
                    }}
                  >
                    <Tooltip title={t("tooltip_live_update_panel")}>
                      <span>
                        <IconButton
                          size="medium"
                          sx={{
                            height: "40px",
                            color: "inherit",
                            "&.Mui-disabled": {
                              color: "inherit",
                              opacity: "0.5",
                            },
                            display:
                              examStatus?.status === t("word_completed") ||
                              examStatus?.status === t("word_terminated")
                                ? "none"
                                : "block",
                            "@keyframes fade-in-out": {
                              "0%": {
                                opacity: "0.3",
                              },
                              "50%": {
                                opacity: "1",
                              },
                              "100%": {
                                opacity: "0.3",
                              },
                            },
                            animation: checkLiveUpdateDisabled()
                              ? "none"
                              : "fade-in-out 2s infinite",
                          }}
                          disabled={checkLiveUpdateDisabled()}
                          onClick={() => {
                            handleViewLiveUpdateClick(device?.id);
                          }}
                          aria-label="live-update-icon-button"
                        >
                          <DiagonalArrowsIcon />
                        </IconButton>
                      </span>
                    </Tooltip>

                    {examMenuButtons
                      ?.slice(0, iconIndexToSlice)
                      ?.map((examButton) => (
                        <Tooltip
                          key={examButton.tooltipText}
                          title={examButton.tooltipText}
                        >
                          <span>
                            <a
                              href={examButton.href}
                              style={{
                                textDecoration: "none",
                                color: "inherit",
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                if (examButton.href) navigate(examButton.href);
                                else examButton.onClick();
                              }}
                            >
                              <IconButton
                                sx={{
                                  height: "40px",
                                  color: "inherit",
                                  "&.Mui-disabled": {
                                    color: "inherit",
                                    opacity: "0.5",
                                  },
                                  display: !examButton.display
                                    ? "none"
                                    : "block",
                                }}
                                disabled={!examButton.display}
                              >
                                {examButton.icon}
                              </IconButton>
                            </a>
                          </span>
                        </Tooltip>
                      ))}

                    {displayEllipsisIcon && (
                      <Tooltip title={t("word_more_actions")}>
                        <span>
                          <IconButton
                            sx={{
                              height: "40px",
                              color: "inherit",
                            }}
                            onClick={examMenuClick}
                          >
                            <EllipsisIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}

                    <ExamMenu
                      examMenu={examMenu}
                      examMenuClose={examMenuClose}
                      examMenuButtons={examMenuButtons?.slice(
                        iconIndexToSlice,
                        examMenuButtons?.length
                      )}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }
);

export default ExamCard;
